import type { GoalSourceContent } from "@/utils/GoalSource";
import type { Curriculum } from "@/models/Curriculum.model";
import { OpStapGoalStructureProvider } from "@/goals/opstap/utils/OpStapGoalStructureProvider";

/** Supplies the display skeleton (GoalSourceContent) for a curriculum. */
export interface GoalStructureProvider {
	/** Return the display skeleton for a curriculum, or null if it cannot be obtained. */
	loadContent(curriculum: Curriculum): Promise<GoalSourceContent | null>;
}

/** Default provider: loads the static /goal-sources/<id>.json skeleton. */
export class StaticJsonGoalStructureProvider implements GoalStructureProvider {

	async loadContent(curriculum: Curriculum): Promise<GoalSourceContent | null> {
		// Production organisation ids match the static file names exactly (verified against
		// production data 2026-07-30), so the id is used as-is.
		const file = '/goal-sources/' + curriculum.id + '.json';
		try {
			const response = await fetch(file);

			// A missing file does not 404: static hosting falls back to the SPA's
			// index.html with status 200, so the content type is the only reliable
			// "no such goal source" signal.
			if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) {
				console.error(`No goal source file for curriculum "${curriculum.id}" (${file})`);
				return null;
			}

			return await response.json();
		} catch (e) {
			console.error(e);
			return null;
		}
	}

}

/** Pick the structure provider for a curriculum. */
export function resolveGoalStructureProvider(curriculum: Curriculum): GoalStructureProvider {
	if (curriculum.key === 'opstap') {
		return new OpStapGoalStructureProvider();
	}
	return new StaticJsonGoalStructureProvider();
}
